enum REQUEST_STATE {
  INIT = "REQUEST_INIT",
  IN_PROGRESS = "REQUEST_IN_PROGRESS",
  ERROR = "REQUEST_ERROR",
  SUCCESS = "REQUEST_SUCCESS",
}

type DataType = "text" | "json" | "blob" | "arrayBuffer" | "formData";
// type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS';

export interface RequestOptions extends Record<string, any> {
  query?: Record<string, any>;
  body?: any;
  baseUrl?: string;
  responseType?: DataType;
}

export interface GlobalRequestOptions {
  baseUrl?: string;
  signal?: AbortController["signal"];
  beforeRequest?: (fetchOptions: RequestInit, requestOptions: RequestOptions) => Promise<void>;
  formatResponse?: (fetchResponse: Response, requestOptions: RequestOptions) => void;
  onError?: (
    fetchError: Response,
    fetchOptions: RequestInit,
    requestOptions: RequestOptions,
    retry: (options?: Record<string, any>
    ) => Promise<unknown>, errorMessageCode: string) => void;
}

export default function useRequest(globalOptions: GlobalRequestOptions) {
  const requestState = ref(REQUEST_STATE.INIT);
  const isError = computed(() => requestState.value === REQUEST_STATE.ERROR);
  const loading = computed(() => requestState.value === REQUEST_STATE.IN_PROGRESS);
  const errorMessageCode = ref("");

  async function makeRequest<T>(url: string, options: RequestOptions = {}, initFetchOptions?: RequestInit): Promise<T> {
    errorMessageCode.value = "";
    requestState.value = REQUEST_STATE.IN_PROGRESS;
    const baseUrl = options.baseUrl || globalOptions.baseUrl || "/";
    const defaultErrorMessage = "default_error_message";
    const defaultFetchOptions: RequestInit = {
      mode: "cors",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    };

    const fetchOptions = Object.assign(defaultFetchOptions, initFetchOptions);
    let fetchUrl = `${baseUrl}${url}`;

    if (options.query) {
      // TODO: Move to helper function
      const queryString = Object.keys(options.query)
        .reduce((acc: any[], key) => {
          const value = options.query && options.query[key];
          if (value !== undefined)
            acc.push(`${key}=${value}`);

          return acc;
        }, [])
        .join("&");

      if (queryString.length)
        fetchUrl = fetchUrl.concat(`?${queryString}`);
    }

    if (options.body)
      fetchOptions.body = JSON.stringify(options.body);

    if (globalOptions?.beforeRequest)
      await globalOptions?.beforeRequest(fetchOptions, options);

    if (globalOptions?.signal)
      fetchOptions.signal = globalOptions.signal;

    return new Promise((resolve, reject) => {
      fetch(fetchUrl, fetchOptions)
        .then(async (fetchResponse) => {
          if (!fetchResponse.ok)
            return Promise.reject(fetchResponse);

          const responseType = options.responseType || "json";

          if (globalOptions?.formatResponse)
            return globalOptions?.formatResponse(fetchResponse, options);

          return fetchResponse[responseType]();
        })
        .then(response => resolve(response))
        .catch(async (fetchResponseError) => {
          requestState.value = REQUEST_STATE.ERROR;

          const responseType = options.responseType || "json";

          if (fetchResponseError[responseType]) {
            const fetchError = await fetchResponseError[responseType]().catch(() => {});

            errorMessageCode.value = fetchError?.messageErrorCode || defaultErrorMessage;
          } else {
            errorMessageCode.value = defaultErrorMessage;
          }

          const retry = <T>(payload?: Record<string, any>): Promise<T> => makeRequest(url, Object.assign({}, options, payload), initFetchOptions);

          if (globalOptions.onError)
            return resolve(globalOptions.onError(fetchResponseError, fetchOptions, options, retry, errorMessageCode.value) as T);

          reject(new Error(errorMessageCode.value));
        })
        .finally(() => {
          if (requestState.value !== REQUEST_STATE.ERROR)
            requestState.value = REQUEST_STATE.SUCCESS;
        });
    });
  }

  const request = {
    get: <T>(url: string, options?: RequestOptions) => makeRequest<T>(url, options, { method: "GET" }),
    post: <T>(url: string, options?: RequestOptions) => makeRequest<T>(url, options, { method: "POST" }),
    put: <T>(url: string, options?: RequestOptions) => makeRequest<T>(url, options, { method: "PUT" }),
    patch: <T>(url: string, options?: RequestOptions) => makeRequest<T>(url, options, { method: "PATCH" }),
    delete: <T>(url: string, options?: RequestOptions) => makeRequest<T>(url, options, { method: "DELETE" }),
  };

  return { isError, loading, errorMessageCode, request };
}
